import { useRef, useState } from "react";
import { Brain } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  AsyncSection,
  asyncStatus,
  Button,
  Select,
  SettingsGroup,
  Skeleton,
  TabPanel,
  Tabs,
  SettingsMetaCard,
  useCursorPagination,
  useRetainedValue,
} from "@playbookai/ui";
import {
  AdminAboutCard,
  AdminRelatedCard,
  AdminSection,
} from "./AdminSection.js";
import { useAuthStore } from "../../auth/auth-store.js";
import { useProgressiveCursorList } from "../../hooks/useProgressiveCursorList.js";
import * as api from "../../api/admin-ai-profiles-api.js";
import { testModelConnection } from "../../api/admin-genai-api.js";
import { AdminApiError } from "../../api/admin-users-api.js";
import {
  ConnectionTestDialog,
  type ConnectionTestRun,
} from "./ai/ConnectionTestDialog.js";
import { ConnectionEditor } from "./ai/ConnectionEditor.js";
import { ProfileTestDialog } from "./ai/ProfileTestDialog.js";
import { ProfileEditor } from "./ai/ProfileEditor.js";
import { DefaultsEditor } from "./ai/DefaultsEditor.js";
import { LifecycleDialog, type LifecycleTarget } from "./ai/LifecycleDialog.js";
import { PagedTable } from "./ai/PagedTable.js";
import { mergeById } from "./ai/merge-by-id.js";
import { connectionColumns as buildConnectionColumns } from "./ai/connection-columns.js";
import { profileColumns as buildProfileColumns } from "./ai/profile-columns.js";
import { ChatDeliverySettings } from "./ChatDeliverySettings.js";

export function secretName(uri: string): string {
  return uri.split("/").filter(Boolean).at(-1) ?? uri;
}

function normalizedPage<T>(
  value: api.AdminCursorPage<T> | readonly T[] | undefined,
): api.AdminCursorPage<T> {
  if (Array.isArray(value)) {
    return { items: [...value], nextCursor: null, hasMore: false };
  }
  return (
    (value as api.AdminCursorPage<T> | undefined) ?? {
      items: [],
      nextCursor: null,
      hasMore: false,
    }
  );
}
export function GenAiAdmin() {
  const tenant = useAuthStore((state) => state.claims?.tenantId);
  const queryKey = ["admin", tenant ?? "none", "genai"];
  const client = useQueryClient();
  const [pageSize, setPageSize] = useState<25 | 50>(() => {
    const value = new URLSearchParams(window.location.search).get("pageSize");
    return value === "50" ? 50 : 25;
  });
  const [includeRetired, setIncludeRetired] = useState(
    () =>
      new URLSearchParams(window.location.search).get("lifecycle") ===
      "include-retired",
  );
  const [availableOnly, setAvailableOnly] = useState(
    () =>
      new URLSearchParams(window.location.search).get("availability") ===
      "available",
  );
  const connectionPaging = useCursorPagination(
    `${tenant ?? "none"}|connections|${pageSize}|${includeRetired}`,
    { pageSize },
  );
  const profilePaging = useCursorPagination(
    `${tenant ?? "none"}|profiles|${pageSize}|${includeRetired}|${availableOnly}`,
    { pageSize },
  );
  const connections = useQuery({
    queryKey: [
      ...queryKey,
      "connections",
      pageSize,
      includeRetired,
      connectionPaging.cursor ?? "first",
    ],
    queryFn: () =>
      api.pageConnections({
        limit: pageSize,
        includeRetired,
        ...(connectionPaging.cursor ? { cursor: connectionPaging.cursor } : {}),
      }),
    placeholderData: (previousData, previousQuery) => {
      const previousKey = previousQuery?.queryKey;
      return previousKey?.[1] === (tenant ?? "none") &&
        previousKey?.[4] === pageSize &&
        previousKey?.[5] === includeRetired
        ? previousData
        : undefined;
    },
  });
  const profiles = useQuery({
    queryKey: [
      ...queryKey,
      "profiles",
      pageSize,
      includeRetired,
      availableOnly,
      profilePaging.cursor ?? "first",
    ],
    queryFn: () =>
      api.pageProfiles({
        limit: pageSize,
        includeRetired,
        availableOnly,
        ...(profilePaging.cursor ? { cursor: profilePaging.cursor } : {}),
      }),
    placeholderData: (previousData, previousQuery) => {
      const previousKey = previousQuery?.queryKey;
      return previousKey?.[1] === (tenant ?? "none") &&
        previousKey?.[4] === pageSize &&
        previousKey?.[5] === includeRetired &&
        previousKey?.[6] === availableOnly
        ? previousData
        : undefined;
    },
  });
  const connectionPage = normalizedPage(
    connections.data as
      | api.AdminCursorPage<api.AdminConnection>
      | api.AdminConnection[]
      | undefined,
  );
  const profilePage = normalizedPage(
    profiles.data as
      | api.AdminCursorPage<api.ExecutionProfile>
      | api.ExecutionProfile[]
      | undefined,
  );
  connectionPaging.reportTotal(connectionPage.totalCount);
  profilePaging.reportTotal(profilePage.totalCount);
  const connectionRows = connectionPage.items;
  const profileRows = profilePage.items;
  const providers = useQuery({
    queryKey: [...queryKey, "providers"],
    queryFn: api.listProviders,
  });
  const defaults = useQuery({
    queryKey: [...queryKey, "defaults"],
    queryFn: api.getDefaults,
  });
  const [connectionEditor, setConnectionEditor] = useState<
    api.AdminConnection | "new" | null
  >(null);
  const [profileEditor, setProfileEditor] = useState<
    api.ExecutionProfile | "new" | null
  >(null);
  const [profileTest, setProfileTest] = useState<api.ExecutionProfile | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState("connections");
  const [initialConnectionId, setInitialConnectionId] = useState<
    string | undefined
  >();
  const [lifecycle, setLifecycle] = useState<LifecycleTarget | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [testRun, setTestRun] = useState<ConnectionTestRun | null>(null);
  const [testOpen, setTestOpen] = useState(false);
  const testingRef = useRef(false);
  const [testEnabling, setTestEnabling] = useState(false);
  const enablingRef = useRef(false);
  const [enableError, setEnableError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{
    error: boolean;
    message: string;
  } | null>(null);
  const currentTest = testRun?.tenant === tenant ? testRun : null;
  /* Each dialog is hoisted onto `open` so it can animate out (ADR-0091), which
     means its body renders for one more frame after the selection is already
     null. Retaining the last selection is what keeps that frame showing what it
     was showing instead of dereferencing null — and the TENANT FENCE is applied
     AFTER the retention, so a handover blanks the dialog rather than holding
     another tenant's connection on screen while it fades. */
  const retainedTest = useRetainedValue(testRun);
  const shownTest = retainedTest?.tenant === tenant ? retainedTest : null;
  const shownProfileTest = useRetainedValue(profileTest);
  const shownLifecycle = useRetainedValue(lifecycle);
  const testRunning =
    currentTest !== null && currentTest.finishedAt === undefined;
  const controlsBusy = !!busy || testRunning || testEnabling;
  const connectionOptions = useProgressiveCursorList({
    queryKey: [...queryKey, "connection-options"],
    enabled: profileEditor !== null,
    fetchPage: async (cursor) => {
      const result = normalizedPage(
        (await api.pageConnections({
          limit: 20,
          ...(cursor ? { cursor } : {}),
        })) as api.AdminCursorPage<api.AdminConnection> | api.AdminConnection[],
      );
      return { items: result.items, nextCursor: result.nextCursor };
    },
  });
  const profileOptions = useProgressiveCursorList({
    queryKey: [...queryKey, "profile-options"],
    enabled: activeTab === "defaults",
    fetchPage: async (cursor) => {
      const result = normalizedPage(
        (await api.pageProfiles({
          limit: 20,
          ...(cursor ? { cursor } : {}),
        })) as
          | api.AdminCursorPage<api.ExecutionProfile>
          | api.ExecutionProfile[],
      );
      return { items: result.items, nextCursor: result.nextCursor };
    },
  });
  const selectableConnections = mergeById(
    connectionRows,
    connectionOptions.items,
  );
  const selectableProfiles = mergeById(profileRows, profileOptions.items);
  async function startTest(connection: api.AdminConnection) {
    if (testingRef.current || enablingRef.current) return;
    testingRef.current = true;
    const run: ConnectionTestRun = {
      tenant,
      connection,
      startedAt: Date.now(),
    };
    setTestRun(run);
    setTestOpen(true);
    setEnableError(null);
    try {
      const result = await testModelConnection(connection.id);
      setTestRun({ ...run, result, finishedAt: Date.now() });
    } catch (cause) {
      setTestRun({
        ...run,
        requestFailed: true,
        errorCode: cause instanceof AdminApiError ? cause.code : undefined,
        finishedAt: Date.now(),
      });
    } finally {
      testingRef.current = false;
      void client.invalidateQueries({ queryKey });
    }
  }
  async function enableTestedConnection() {
    if (!currentTest || enablingRef.current) return;
    enablingRef.current = true;
    setTestEnabling(true);
    setEnableError(null);
    setNotice(null);
    try {
      // A recorded test increments the connection version. Enable against a
      // fresh row, never the version captured before the test began.
      const latest = await api.getConnection(currentTest.connection.id);
      if (latest.last_test_status !== "passed") {
        setEnableError(
          "The connection has changed or no longer has a passing test. Refresh and test it again.",
        );
        return;
      }
      if (latest.status !== "active")
        await api.changeLifecycle("connections", latest, "enable");
      setTestOpen(false);
      setNotice({ error: false, message: "Connection enabled." });
    } catch {
      setEnableError(
        "Could not enable the connection. Refresh its status and try again.",
      );
    } finally {
      enablingRef.current = false;
      setTestEnabling(false);
      void client.invalidateQueries({ queryKey });
    }
  }

  const refresh = () => {
    void client.invalidateQueries({ queryKey });
  };
  async function action(
    id: string,
    execute: () => Promise<unknown>,
    message: string,
  ) {
    setBusy(id);
    setNotice(null);
    try {
      await execute();
      setNotice({ error: false, message });
    } catch (cause) {
      setNotice({
        error: true,
        message:
          cause instanceof Error ? cause.message : "Could not update AIS.",
      });
    } finally {
      setBusy(null);
      refresh();
    }
  }
  const connectionColumns = buildConnectionColumns({
    providers,
    setConnectionEditor,
    setProfileEditor,
    setInitialConnectionId,
    setLifecycle,
    controlsBusy,
    startTest,
    action,
  });
  const profileColumns = buildProfileColumns({
    setProfileEditor,
    setProfileTest,
    setLifecycle,
    controlsBusy,
    action,
  });
  return (
    <AdminSection
      icon={Brain}
      title="GenAI configuration"
      description="Manage connections, execution profiles, and app defaults."
      meta={
        <>
          {/* Counts from the reads the tabs already make (d4). */}
          <SettingsMetaCard
            icon={Brain}
            title="Configuration"
            rows={[
              {
                label: "Connections",
                value:
                  connectionPaging.totalCount === undefined
                    ? "…"
                    : String(connectionPaging.totalCount),
              },
              {
                label: "Enabled shown",
                value: connections.data
                  ? String(
                      connectionRows.filter((row) => row.status === "active")
                        .length,
                    )
                  : "…",
              },
              {
                label: "Execution profiles",
                value:
                  profilePaging.totalCount === undefined
                    ? "…"
                    : String(profilePaging.totalCount),
              },
            ]}
          />
          <AdminAboutCard title="About AI configuration">
            Connections and execution profiles for the AI service (AIS). Modules
            reach AI only through AIS, by intent: no module names a model or a
            provider, so a connection can change here without a product change.
          </AdminAboutCard>
          <AdminRelatedCard ids={["health", "integrations"]} />
        </>
      }
    >
      <div className="grid min-w-0 gap-6">
        {notice && (
          <Alert tone={notice.error ? "critical" : "healthy"}>
            {notice.message}
          </Alert>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-40">
            <Select
              label="Rows per page"
              value={String(pageSize)}
              options={[
                { value: "25", label: "25 rows" },
                { value: "50", label: "50 rows" },
              ]}
              onChange={(value) => {
                const nextSize = value === "50" ? 50 : 25;
                const params = new URLSearchParams(window.location.search);
                params.set("pageSize", String(nextSize));
                window.history.replaceState(
                  window.history.state,
                  "",
                  `${window.location.pathname}?${params.toString()}${window.location.hash}`,
                );
                setPageSize(nextSize);
              }}
            />
          </div>
          <div className="w-44">
            <Select
              label="Lifecycle"
              value={includeRetired ? "include-retired" : "current"}
              options={[
                { value: "current", label: "Current only" },
                { value: "include-retired", label: "Include retired" },
              ]}
              onChange={(value) => {
                const enabled = value === "include-retired";
                const params = new URLSearchParams(window.location.search);
                if (enabled) params.set("lifecycle", "include-retired");
                else params.delete("lifecycle");
                window.history.replaceState(
                  window.history.state,
                  "",
                  `${window.location.pathname}?${params.toString()}${window.location.hash}`,
                );
                setIncludeRetired(enabled);
              }}
            />
          </div>
          <div className="w-48">
            <Select
              label="Profile availability"
              value={availableOnly ? "available" : "all"}
              options={[
                { value: "all", label: "All profiles" },
                { value: "available", label: "Available only" },
              ]}
              onChange={(value) => {
                const enabled = value === "available";
                const params = new URLSearchParams(window.location.search);
                if (enabled) params.set("availability", "available");
                else params.delete("availability");
                window.history.replaceState(
                  window.history.state,
                  "",
                  `${window.location.pathname}?${params.toString()}${window.location.hash}`,
                );
                setAvailableOnly(enabled);
              }}
            />
          </div>
        </div>
        <Tabs
          className="min-w-0 [&>[role=tablist]]:flex-wrap"
          aria-label="GenAI configuration"
          value={activeTab}
          onValueChange={setActiveTab}
          items={[
            { value: "connections", label: "Connections" },
            { value: "profiles", label: "Profiles" },
            { value: "defaults", label: "App defaults" },
          ]}
        >
          <TabPanel value="connections">
            <SettingsGroup
              title="Connections"
              titleHidden
              description="Save provider access once, then create and test model-specific profiles."
            >
              {/* The provider list only gates the Create button. It is reported
                  here, beside the control it disables, and ONLY when the table
                  is not already reporting the same outage — `providers` and
                  `connections` are two queries against one service, so
                  reporting each on its own printed the identical server string
                  twice, one copy with a Retry and one without. */}
              {providers.error && !connections.error ? (
                <Alert
                  tone="critical"
                  title="Couldn't load the provider list"
                  className="mb-4"
                >
                  {providers.error.message} You can still edit the connections
                  below, but not create a new one until this loads.
                  <Button
                    variant="secondary"
                    onClick={() => {
                      void providers.refetch();
                    }}
                  >
                    Retry
                  </Button>
                </Alert>
              ) : null}
              <div className="mb-4">
                <Button
                  variant="primary"
                  disabled={!providers.data?.length || controlsBusy}
                  onClick={() => setConnectionEditor("new")}
                >
                  Create connection
                </Button>
              </div>
              <PagedTable
                rows={connectionRows}
                columns={connectionColumns}
                id={(row) => row.id}
                caption="Connections"
                loading={connections.isPending}
                error={
                  connections.error
                    ? {
                        title: "Couldn't load the connections",
                        description: connections.error.message,
                        onRetry: () => {
                          void connections.refetch();
                          void providers.refetch();
                        },
                      }
                    : undefined
                }
                paging={{
                  page: connectionPaging.page,
                  totalCount: connectionPaging.totalCount,
                  canGoBack: connectionPaging.canGoBack,
                  hasNext: connectionPage.hasMore,
                  busy: connections.isFetching,
                  onPrev: connectionPaging.goBack,
                  onNext: () => {
                    if (connectionPage.nextCursor) {
                      connectionPaging.goNext(connectionPage.nextCursor);
                    }
                  },
                }}
              />
            </SettingsGroup>
          </TabPanel>
          <TabPanel value="profiles">
            <SettingsGroup
              title="Execution profiles"
              titleHidden
              description="A profile combines a connection with reasoning and generation settings. All available profiles can be selected by tenant members."
            >
              <div className="mb-4">
                <Button
                  variant="primary"
                  disabled={controlsBusy}
                  onClick={() => {
                    setInitialConnectionId(undefined);
                    setProfileEditor("new");
                  }}
                >
                  Create profile
                </Button>
              </div>
              <PagedTable
                rows={profileRows}
                columns={profileColumns}
                id={(row) => row.id}
                caption="Profiles"
                loading={profiles.isPending}
                error={
                  profiles.error
                    ? {
                        title: "Couldn't load the execution profiles",
                        description: profiles.error.message,
                        onRetry: () => {
                          void profiles.refetch();
                        },
                      }
                    : undefined
                }
                paging={{
                  page: profilePaging.page,
                  totalCount: profilePaging.totalCount,
                  canGoBack: profilePaging.canGoBack,
                  hasNext: profilePage.hasMore,
                  busy: profiles.isFetching,
                  onPrev: profilePaging.goBack,
                  onNext: () => {
                    if (profilePage.nextCursor) {
                      profilePaging.goNext(profilePage.nextCursor);
                    }
                  },
                }}
              />
            </SettingsGroup>
          </TabPanel>
          <TabPanel value="defaults">
            <SettingsGroup
              title="App defaults"
              titleHidden
              description="Choose explicit defaults or let apps inherit the platform default."
            >
              {/* Not a table, so the four states come from AsyncSection. The
                  editor needs BOTH queries, so the region has one status: a
                  failure of either is one failure, named once. */}
              <AsyncSection
                status={asyncStatus({
                  isLoading: defaults.isPending || profiles.isPending,
                  isError: Boolean(defaults.error || profiles.error),
                  isEmpty: false,
                  hasData: Boolean(defaults.data && profiles.data),
                })}
                loadingLabel="the app defaults"
                errorTitle="Couldn't load the app defaults"
                errorDescription={(defaults.error ?? profiles.error)?.message}
                onRetry={() => {
                  void defaults.refetch();
                  void profiles.refetch();
                }}
                skeleton={
                  <div className="space-y-3 pt-1">
                    <Skeleton className="h-9 w-full" />
                    <Skeleton className="h-9 w-full" />
                    <Skeleton className="h-9 w-2/3" />
                  </div>
                }
              >
                {defaults.data && profiles.data ? (
                  <div className="pt-1">
                    <DefaultsEditor
                      defaults={defaults.data}
                      profiles={selectableProfiles}
                      hasMoreProfiles={profileOptions.hasMore}
                      loadingMoreProfiles={profileOptions.isLoadingMore}
                      profileLoadError={profileOptions.error?.message}
                      onLoadMoreProfiles={profileOptions.loadMore}
                      onRetryProfiles={profileOptions.refetch}
                      onSaved={refresh}
                    />
                  </div>
                ) : null}
              </AsyncSection>
            </SettingsGroup>
          </TabPanel>
        </Tabs>
        <ChatDeliverySettings />
        {shownTest && (
          <ConnectionTestDialog
            open={testOpen && currentTest !== null}
            key={shownTest.startedAt}
            run={shownTest}
            providerLabel={
              providers.data?.find(
                (item) => item.id === shownTest.connection.provider_key,
              )?.label ?? shownTest.connection.provider_key
            }
            enabled={
              connectionRows.find((row) => row.id === shownTest.connection.id)
                ?.status === "active"
            }
            enabling={testEnabling}
            enableError={enableError}
            onEnable={() => {
              void enableTestedConnection();
            }}
            onRetry={() => {
              void startTest(shownTest.connection);
            }}
            onClose={() => {
              setTestOpen(false);
              setEnableError(null);
            }}
          />
        )}
        {shownProfileTest && (
          <ProfileTestDialog
            open={profileTest !== null}
            key={shownProfileTest.id}
            profile={shownProfileTest}
            queryKey={queryKey}
            onSaved={refresh}
            onClose={() => setProfileTest(null)}
          />
        )}
        {connectionEditor && (
          <ConnectionEditor
            connection={connectionEditor === "new" ? null : connectionEditor}
            providers={providers.data ?? []}
            onSaved={refresh}
            onClose={() => setConnectionEditor(null)}
          />
        )}
        {profileEditor && (
          <ProfileEditor
            initialConnectionId={initialConnectionId}
            profile={profileEditor === "new" ? null : profileEditor}
            connections={selectableConnections}
            hasMoreConnections={connectionOptions.hasMore}
            loadingMoreConnections={connectionOptions.isLoadingMore}
            connectionLoadError={connectionOptions.error?.message}
            onLoadMoreConnections={connectionOptions.loadMore}
            onRetryConnections={connectionOptions.refetch}
            queryKey={queryKey}
            onSaved={refresh}
            onClose={() => setProfileEditor(null)}
          />
        )}
        {shownLifecycle && (
          <LifecycleDialog
            open={lifecycle !== null}
            key={`${shownLifecycle.kind}:${shownLifecycle.row.id}:${shownLifecycle.action}`}
            target={shownLifecycle}
            profiles={profileRows}
            queryKey={queryKey}
            onSaved={refresh}
            onClose={() => setLifecycle(null)}
          />
        )}
      </div>
    </AdminSection>
  );
}
